import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Mail, Phone, Trash2, Crown, Shield, User } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSubscription } from '@/hooks/useSubscription';
import { toast } from 'sonner';

interface FamilyGroup {
  id: string;
  name: string;
  description: string;
  owner_id: string;
}

interface FamilyMember {
  id: string;
  family_group_id: string;
  user_id: string | null;
  email: string | null;
  phone: string | null;
  name: string | null;
  role: string;
  status: string;
  invited_at: string;
  joined_at: string | null;
}

interface FamilyManagementProps {
  familyGroups: FamilyGroup[];
  onUpdate: () => void;
}

export const FamilyManagement = ({ familyGroups, onUpdate }: FamilyManagementProps) => {
  const { user } = useAuth();
  const { checkCanAddFamilyMembers } = useSubscription();
  const [selectedGroup, setSelectedGroup] = useState<string>(familyGroups[0]?.id || '');
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'member'
  });

  const loadMembers = async (groupId: string) => {
    if (!groupId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('family_memberships')
        .select('*')
        .eq('family_group_id', groupId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setMembers(data || []);
    } catch (error) {
      console.error('Error loading members:', error);
      toast.error('Failed to load family members');
    } finally {
      setLoading(false);
    }
  };

  // Load members for the first selected group
  useEffect(() => {
    if (selectedGroup) {
      loadMembers(selectedGroup);
    }
  }, [selectedGroup]);

  const handleGroupChange = (groupId: string) => {
    setSelectedGroup(groupId);
    loadMembers(groupId);
  };

  const inviteMember = async () => {
    if (!selectedGroup || !user) return;

    const { canAdd, reason } = checkCanAddFamilyMembers();
    if (!canAdd) {
      toast.error(reason);
      return;
    }

    if (!inviteForm.name || !inviteForm.email) {
      toast.error('Please provide a name and email address');
      return;
    }

    try {
      setLoading(true);
      
      // Insert the family membership
      const { data: membership, error } = await supabase
        .from('family_memberships')
        .insert({
          family_group_id: selectedGroup,
          name: inviteForm.name,
          email: inviteForm.email || null,
          phone: inviteForm.phone || null,
          role: inviteForm.role,
          status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;

      // Send email invitation if email is provided
      if (inviteForm.email) {
        // Get family group name
        const selectedFamilyGroup = familyGroups.find(group => group.id === selectedGroup);
        
        const { error: emailError } = await supabase.functions.invoke('send-family-invitation', {
          body: {
            memberEmail: inviteForm.email,
            memberName: inviteForm.name,
            familyGroupName: selectedFamilyGroup?.name || 'Family Group',
            inviterName: user?.user_metadata?.display_name || user?.email || 'Family Member',
            role: inviteForm.role,
            invitationId: membership.id,
          },
        });

        if (emailError) {
          console.error("Error sending family invitation email:", emailError);
          toast.success('Family member invited! (Email delivery failed - they can still join manually)');
        } else {
          toast.success('Family member invited and email sent successfully!');
        }
      } else {
        toast.success('Family member invited successfully! (No email - share invitation manually)');
      }

      setInviteDialogOpen(false);
      setInviteForm({ name: '', email: '', phone: '', role: 'member' });
      loadMembers(selectedGroup);
      onUpdate();
    } catch (error) {
      console.error('Error inviting member:', error);
      toast.error('Failed to invite family member');
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (memberId: string) => {
    if (!confirm('Are you sure you want to remove this family member?')) return;

    try {
      const { error } = await supabase
        .from('family_memberships')
        .delete()
        .eq('id', memberId);

      if (error) throw error;

      toast.success('Family member removed successfully!');
      loadMembers(selectedGroup);
      onUpdate();
    } catch (error) {
      console.error('Error removing member:', error);
      toast.error('Failed to remove family member');
    }
  };

  const updateMemberRole = async (memberId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('family_memberships')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;

      toast.success('Member role updated successfully!');
      loadMembers(selectedGroup);
    } catch (error) {
      console.error('Error updating member role:', error);
      toast.error('Failed to update member role');
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-blue-500" />;
      default:
        return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="default">Active</Badge>;
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'inactive':
        return <Badge variant="outline">Inactive</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (familyGroups.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">No family groups to manage</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Family Management</CardTitle>
          <CardDescription>Manage your family groups and members</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="group-select">Select Family Group</Label>
              <Select value={selectedGroup} onValueChange={handleGroupChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a family group" />
                </SelectTrigger>
                <SelectContent>
                  {familyGroups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedGroup && (
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Family Members</h3>
                <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button>
                      <UserPlus className="h-4 w-4 mr-2" />
                      Invite Member
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Invite Family Member</DialogTitle>
                      <DialogDescription>
                        Send an email invitation to join your family group on Event Date Picker
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="name">Name *</Label>
                        <Input
                          id="name"
                          value={inviteForm.name}
                          onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                          placeholder="Enter member name"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={inviteForm.email}
                          onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                          placeholder="Enter email address (required for invitation)"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          value={inviteForm.phone}
                          onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                          placeholder="Enter phone number"
                        />
                      </div>
                      <div>
                        <Label htmlFor="role">Role</Label>
                        <Select value={inviteForm.role} onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setInviteDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={inviteMember} disabled={loading}>
                        {loading ? 'Inviting...' : 'Send Invite'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {selectedGroup && (
        <Card>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading members...</div>
            ) : members.length > 0 ? (
              <div className="space-y-4">
                {members.map((member) => (
                  <div key={member.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getRoleIcon(member.role)}
                      <div>
                        <h4 className="font-medium">{member.name || 'Unnamed Member'}</h4>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {member.email && (
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {member.email}
                            </span>
                          )}
                          {member.phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {member.phone}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(member.status)}
                      <Badge variant="outline" className="capitalize">
                        {member.role}
                      </Badge>
                      {member.role !== 'owner' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeMember(member.id)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <UserPlus className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No family members yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};